import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, assetUrl } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import PostCard from '../components/PostCard.jsx';
import AvailabilityCalendar from '../components/AvailabilityCalendar.jsx';
import LanguageSelector from '../components/LanguageSelector.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

export default function CaddieProfile() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useLocale();

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ workplace: '', phone: '', bio: '', languages: [] });
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const avatarInputRef = useRef(null);

  const isOwn = user?.sub === id || user?.id === id;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      isOwn ? api.users.me() : api.users.getById(id),
      api.posts.feed(1, { user_id: id }),
      api.caddies.getAvailability(id, month),
    ])
      .then(([u, feedData, avData]) => {
        if (u.role !== 'caddie') {
          navigate(`/golfers/${id}`, { replace: true });
          return;
        }
        setProfile(u);
        setEditForm({
          workplace: u.profile?.workplace ?? '',
          phone: u.profile?.phone ?? '',
          bio: u.profile?.bio ?? '',
          languages: u.profile?.languages ?? [],
        });
        setPosts(feedData.posts ?? []);
        setAvailability(avData.availability ?? []);
      })
      .catch(() => navigate('/feed'))
      .finally(() => setLoading(false));
  }, [id, isOwn, month, navigate]);

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const { avatar_url } = await api.users.uploadAvatar(fd);
      setProfile((p) => ({ ...p, avatar_url }));
      await refreshUser();
    } catch (err) {
      alert(err.message);
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleProfileSave() {
    setSaving(true);
    try {
      await api.caddies.updateProfile(editForm);
      setProfile((p) => ({ ...p, profile: editForm }));
      setEditMode(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAvailabilitySave(data) {
    await api.caddies.setAvailability(data);
    const avData = await api.caddies.getAvailability(id, month);
    setAvailability(avData.availability ?? []);
  }

  async function handleFollow() {
    try {
      const { following } = await api.caddies.toggleFollow(id);
      setProfile((current) => ({ ...current, is_following: following }));
    } catch (err) {
      alert(err.message);
    }
  }

  function handleDeletePost(deletedId) {
    if (!window.confirm('Delete this post?')) return;
    api.posts.delete(deletedId).then(() => {
      setPosts((prev) => prev.filter((p) => p.id !== deletedId));
    }).catch(console.error);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Profile header */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {/* Avatar */}
          <div className="relative shrink-0">
            {profile.avatar_url ? (
              <img src={assetUrl(profile.avatar_url)} alt={profile.display_name} className="w-24 h-24 rounded-full object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-3xl font-bold">
                {profile.display_name?.[0]?.toUpperCase()}
              </div>
            )}
            {isOwn && (
              <>
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="absolute bottom-0 right-0 bg-brand-700 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm shadow hover:bg-brand-800"
                  title="Change photo"
                >
                  {avatarUploading ? '…' : '📷'}
                </button>
                <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
              </>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-xl font-bold text-gray-900">{profile.display_name}</h1>
                <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-brand-100 text-brand-700">
                  {profile.role === 'caddie' ? '🎒 Caddie' : '🏌️ Golfer'}
                </span>
              </div>
              <div className="flex gap-2">
                {isOwn ? (
                  <button
                    onClick={() => setEditMode((v) => !v)}
                    className="btn-secondary text-sm"
                  >
                    {editMode ? t('cancel') : t('editProfile')}
                  </button>
                ) : (
                  <>
                    {user?.role === 'golfer' && profile.role === 'caddie' && (
                      <button onClick={handleFollow} className="btn-secondary text-sm">
                        {profile.is_following ? t('following') : t('follow')}
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/messages/${id}`)}
                      className="btn-primary text-sm"
                    >
                      💬 {t('message')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Edit form */}
            {isOwn && editMode ? (
              <div className="mt-4 space-y-3">
                <input className="input text-sm" placeholder="Workplace / Golf course" value={editForm.workplace} onChange={(e) => setEditForm((f) => ({ ...f, workplace: e.target.value }))} />
                <input className="input text-sm" placeholder={t('privatePhone')} value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
                <div>
                  <p className="mb-1.5 text-sm font-medium text-gray-700">{t('languagesSpoken')}</p>
                  <LanguageSelector value={editForm.languages} onChange={(languages) => setEditForm((f) => ({ ...f, languages }))} />
                </div>
                <textarea className="input text-sm resize-none min-h-[80px]" placeholder="Bio" value={editForm.bio} onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))} maxLength={500} />
                <button onClick={handleProfileSave} disabled={saving} className="btn-primary text-sm">
                  {saving ? t('saving') : t('saveChanges')}
                </button>
              </div>
            ) : (
              <div className="mt-3 space-y-1 text-sm text-gray-600">
                {profile.profile?.workplace && <p>🏌️ {profile.profile.workplace}</p>}
                {isOwn && profile.profile?.phone && <p>📞 {profile.profile.phone}</p>}
                {profile.profile?.languages?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {profile.profile.languages.map((language) => (
                      <span key={language} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                        {language}
                      </span>
                    ))}
                  </div>
                )}
                {profile.profile?.bio && <p className="mt-2 text-gray-700 whitespace-pre-wrap">{profile.profile.bio}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Posts grid (2/3 width on lg) */}
        <div className="lg:col-span-2">
          <h2 className="font-semibold text-gray-800 mb-3">{t('posts')} ({posts.length})</h2>
          {posts.length === 0 ? (
            <div className="card p-10 text-center text-gray-400">
              <p>{t('noPosts')}</p>
              {isOwn && (
                <button onClick={() => navigate('/upload')} className="btn-primary mt-3 text-sm">
                  + Add your first post
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} onDelete={isOwn ? handleDeletePost : undefined} />
              ))}
            </div>
          )}
        </div>

        {/* Availability calendar (1/3 width on lg) */}
        {profile.role === 'caddie' && (
          <div className="card p-5">
            <h2 className="font-semibold text-gray-800 mb-3">{t('availability')}</h2>
            <AvailabilityCalendar
              availability={availability}
              editable={isOwn}
              onSave={isOwn ? handleAvailabilitySave : undefined}
              onMonthChange={setMonth}
            />
          </div>
        )}
      </div>
    </div>
  );
}
